// The rows behind a list, kept so coming back from a row can rebuild the list
// before the offset is restored onto it. Used with useScrollRestore, which owns
// the offset itself.
//
// Two different lists need this for two different reasons:
//
//   /search infinite-scrolls, so a fresh mount holds one page of 24 rows and
//   simply isn't tall enough to scroll back to row 90.
//
//   People's suggestions are RANKED and mutable — a refetch can come back in a
//   different order, or shorter once somebody's been added or dismissed. The
//   offset would be honoured perfectly and still land beside a different person,
//   which reads as broken. The list has to be the same list.
//
// Keys scope a list: /search is three lists behind one URL, and restoring
// People's rows into Events would be worse than not restoring.
const LIST_KEY = 'pr.list.cache.v1';

// How many lists to keep. Enough that flipping between two tabs and coming back
// still restores both, small enough that a session can't grow into a quota
// error — each entry is a page of cards, not a number.
const MAX_ENTRIES = 4;

// In-memory mirror: the truth mid-session (survives SPA nav) and a parse cache.
let mem;

function readAll() {
  if (mem !== undefined) return mem;
  try { mem = JSON.parse(sessionStorage.getItem(LIST_KEY) || '{}') || {}; } catch { mem = {}; }
  return mem;
}

function writeAll(next) {
  mem = next;
  // A page of event cards is not small. On a quota error keep the in-memory
  // copy — that still restores across SPA navigation, which is the case this is
  // for; only a hard reload loses it.
  try { sessionStorage.setItem(LIST_KEY, JSON.stringify(next)); } catch { /* quota / disabled */ }
}

/** One list per view+query. A blank query is the browse list. */
export function searchKey(view, q) {
  return `${view || 'events'}|${(q || '').trim().toLowerCase()}`;
}

/** The cached rows for this key ({ items, ... }), or null. */
export function getListCache(key) {
  return readAll()[key] || null;
}

export function saveListCache(key, patch) {
  const all = readAll();
  const next = { ...all, [key]: { ...(all[key] || {}), ...patch, at: Date.now() } };
  // Evict the least recently written once we're over the cap.
  const keys = Object.keys(next);
  if (keys.length > MAX_ENTRIES) {
    keys.sort((a, b) => (next[a].at || 0) - (next[b].at || 0))
      .slice(0, keys.length - MAX_ENTRIES)
      .forEach((k) => delete next[k]);
  }
  writeAll(next);
}
